<template>
  <div>
    <EntryFeiActions ref="entryFeiActions" />
    <v-card color="primary" class="fipa--header" tile>
      {{ $t("people:personPage.upcoming.registrations.heading") }}
    </v-card>
    <DesktopTable v-if="$vuetify.display.mdAndUp" v-bind="{ state, actions }" />
    <MobileTable v-else v-bind="{ state, actions }" />
  </div>
</template>
<script>
import { provide, useTemplateRef } from "vue";
import gql from "fraql";
import { get, cloneDeep } from "lodash-es";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useRouteScroll } from "@composables/route_scroll.ts";
import { useSectionHelper } from "@composables/masterdata/competitions/sections";
import {
  provideEntryFeiActionsInjections,
  provideEntryFeiEntryEditFragment
} from "@elements/AppEntryFeiActions/compositions";

import DesktopTable from "./DesktopTable.vue";
import MobileTable from "./MobileTable.vue";

import AppShowPeopleList from "@AppShows/person/AppShowPeopleList.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import AppShowBeddingType from "@AppShows/horse/AppShowBeddingType.vue";
import AppShowDisciplineName from "@AppShows/AppShowDisciplineName.vue";
import RegistrationClasses from "./RegistrationClasses.vue";

import AppHorseSlot from "@elements/AppHorseSlot.vue";
import AppShowMappedFeiCompetitionCodes from "@AppShows/event/AppShowMappedFeiCompetitionCodes.vue";
import AppShowQuotaType from "@AppShows/event/AppShowQuotaType.vue";
import AppShowEvent from "@AppShows/event/AppShowEvent.vue";
import AppShowEntryFeiFlags from "@AppShows/event/AppShowEntryFeiFlags.vue";
import AppShowEntryBillingContacts from "@AppShows/event/AppShowEntryBillingContacts.vue";
import PrizeMoneyPayoutContactMasterlistFragments from "@elements/contact/prize_money_payout_fragments";
import AppShowDrivingGrooms from "@AppShows/event/AppShowDrivingGrooms.vue";
import EntryFeiActions from "@elements/AppEntryFeiActions/EntryFeiActions.vue";
import AppShowEntryCompetitionDetails from "@AppShows/event/AppShowEntryCompetitionDetails.vue";

import AppShowEntryProducts from "@AppShows/event/AppShowEntryProducts.vue";
import { showLoadingButton } from "@/utils/showLoadingButton";

const registrationFragment = gql`
  fragment _ on Registration {
    id
    earliestPaymentDeadline
    teamName
    paradressageGrade
    ${RegistrationClasses.fragments.registrationFields}
    carriageNumber
    drivingCarriageWidth
    allowedMutations
    logisticNotes
    registrantNotes
    
    ${PrizeMoneyPayoutContactMasterlistFragments.registrationFields}
    prizeMoneyPayoutContact {
      id
      ${PrizeMoneyPayoutContactMasterlistFragments.prizeMoneyPayoutContactFields}
    }

    ${AppShowEntryBillingContacts.fragments.registrationFields}
    vaultingLungerPersonId
    vaultingLungerPerson {
      id
      name
      licenceCategoryId
      ${AppShowPeopleList.fragments.person}
    }
    vaultingReserveLungerPersonId
    vaultingReserveLungerPerson {
      id
      name
      licenceCategoryId
      ${AppShowPeopleList.fragments.person}
    }
    ${AppShowDrivingGrooms.fragments.registrationFields}
    athletes {
      id
      name
      licenceCategoryId
      ${AppShowPeopleList.fragments.person}
    }
    registrationPeople {
      id
      personId
      ${AppShowEntryFeiFlags.fragments.registrationPersonFields}
    }
    registrantPerson {
      id
      ${AppShowPeopleList.fragments.person}
      allowedMutations
    }
    ${AppShowBeddingType.fragments.registrationFields}
    horses {
      id
      ${AppShowHorseName.fragments.horse}
      ${AppHorseSlot.fragments.horseFields}
    }
    registrationHorses {
      id
      horseId
      beddingType
      ${AppShowEntryFeiFlags.fragments.registrationHorseFields}
    }
    event {
      id
      name
      startDate
      endDate
      registrationEndDate
      ${AppShowEvent.fragments.event}
      ${AppShowEntryBillingContacts.fragments.eventFields}
      disciplineId
      discipline @client {
        ${AppShowDisciplineName.fragments.discipline}
      }
      ${RegistrationClasses.fragments.eventFields}
      noncompetition
      useOnlinePayments
      useOnlinePaymentsForPrizeMoney
    }
    entries {
      id
      billingContact {
        ${AppShowEntryBillingContacts.fragments.entryBillingContactFields}
      }
      paymentStatus
      paidAt
      unregisteredAt
      teamName
      ${AppShowEntryCompetitionDetails.fragments.entryFields}
      ${RegistrationClasses.fragments.entry}
      ${AppShowMappedFeiCompetitionCodes.fragments.entryFields}
      ${AppShowQuotaType.fragments.entryFields}
      registrationId
      competition {
        id
        code
        ${AppShowEntryCompetitionDetails.fragments.competitionFields}
        ${AppShowEntryBillingContacts.fragments.competitionFields}
        name
        paidExternally
        isAdministrative
        ${AppShowMappedFeiCompetitionCodes.fragments.competitionFields}
        ${RegistrationClasses.fragments.competition}
        ${AppShowQuotaType.fragments.competitionFields}
        competitionRuleId
        competitionRule @client {
          id
          icelandicDirectionRequired
        }
      }
      paidExternally
      registrantComment
      noteToFeiOrganiser
      icelandicDirection
      phasesToParticipate
      createdAt
      updatedAt
    }

    registrationEventProducts {
      id
      paymentStatus
      paidExternally
      paidAt
      createdAt
      updatedAt
      cancelledAt

      entry {
        id
        ${AppShowEntryProducts.fragments.entryFields}
      }
      eventProduct {
        id
        name

      }
    }
}`;

export default {
  name: "UpcomingRegisteredEvents",
  components: {
    DesktopTable,
    MobileTable,
    EntryFeiActions
  },
  props: {
    registrations: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    personId: {
      type: String,
      default: null
    },
    horseId: {
      type: String,
      default: null
    }
  },
  emits: ["refetch"],
  setup() {
    const { highlightElement, routeScrollParam } = useRouteScroll();

    const entryFeiActions = useTemplateRef("entryFeiActions");
    provide("EntryFeiActionOpener", (ref, props) => {
      entryFeiActions.value.openEntryFeiAction(ref, props);
    });

    provideEntryFeiActionsInjections({
      registrationFragment: () => registrationFragment,
      billingContactFragment: () => gql`
          fragment _ on Contact {
            ${AppShowEntryBillingContacts.fragments.entryBillingContactFields}
          }
        `,
      horseFragment: () => gql`
          fragment _ on Horse {
            ${AppShowHorseName.fragments.horse}
            ${AppHorseSlot.fragments.horseFields}
          }
        `
    });

    provideEntryFeiEntryEditFragment(gql`
      fragment _ on Entry {
        registration {
          id
          vaultingLungerPersonId
          vaultingLungerPerson {
            id
            name
            licenceCategoryId
            ${AppShowPeopleList.fragments.person}
          }
          vaultingReserveLungerPersonId
          vaultingReserveLungerPerson {
            id
            name
            licenceCategoryId
            ${AppShowPeopleList.fragments.person}
          }
          ${AppShowDrivingGrooms.fragments.registrationFields}
        }
      }
    `);

    return {
      ...getFederationConfiguration(),
      ...useSectionHelper(),
      highlightElement,
      routeScrollParam
    };
  },
  data() {
    return {
      expanded: []
    };
  },
  computed: {
    state() {
      return Object.defineProperties(
        {},
        {
          registrations: {
            get: () => {
              return cloneDeep(this.registrations).sort(
                (a, b) =>
                  // From earliest to future
                  new Date(a.event.startDate) - new Date(b.event.startDate)
              );
            }
          },
          expanded: {
            get: () => this.expanded
          },
          loading: {
            get: () => this.loading
          },
          personId: {
            get: () => this.personId
          },
          horseId: {
            et: () => this.horseId
          }
        }
      );
    },
    actions() {
      return {
        deleteItem: this.deleteItem,
        allPaid: this.allPaid,
        registrantComments: this.registrantComments,
        expand: this.expand,
        allEntriesExternal: this.allEntriesExternal,
        timestamps: this.timestamps,
        isFeatureFlagSet: this.isFeatureFlagSet
      };
    }
  },
  watch: {
    loading(v) {
      if (!v) {
        this.scrollInto();
      }
    }
  },
  methods: {
    scrollInto() {
      if (this.$route.hash != "#upcoming" || !this.routeScrollParam()) return;
      this.$nextTick(() => {
        const el = this.$el.querySelector(
          this.routeScrollParam().querySelector
        );
        if (el) {
          const expand = el.querySelector("button.btn-focus");
          if (expand) expand.click();

          el.scrollIntoView({ block: "center" });
          this.highlightElement(el);
        }
      });
    },
    deleteItem(item, evt) {
      let content = `${this.$t("events:unregister.modal.question")}`;
      content += `<br><b>${this.$t(
        "events:unregister.modal.athlete.label"
      )} ${this.showDisplayName(item.athletes[0])}</b>
      `;

      if (item.horses[0]) {
        content += `<br><b>${this.$t("events:unregister.modal.horse.label")} ${
          item.horses[0].name
        }</b> `;
      }

      this.$root
        .$confirm(this.$t("events:unregister.modal.heading"), content)
        .then(async confirm => {
          if (confirm) {
            const removeLoading = showLoadingButton(evt.target);

            this.$apollo
              .mutate({
                mutation: gql`
                  mutation registrationUnregister($id: ID!) {
                    registrationUnregister(input: { id: $id }) {
                      errors
                    }
                  }
                `,
                variables: { id: item.id }
              })
              .then(({ data }) => {
                removeLoading();
                if (
                  data.registrationUnregister.errors &&
                  data.registrationUnregister.errors.length > 0
                ) {
                  this.$root.$snackbarServerError(data.errors, {
                    level: "error"
                  });
                } else {
                  this.$emit("refetch");
                  this.$root.$snackbar("success", {
                    text: "Succesfully unregistered!"
                  });
                }
              })
              .catch(error => {
                // Error
                removeLoading();
                this.$root.$snackbarServerError(error, {
                  level: "error"
                });
              });
          }
        });
    },
    allEntriesExternal(entries) {
      if (entries.length == 0) return false;
      return entries.every(
        e => e.paidExternally || e.competition.paidExternally
      );
    },
    allPaid(entries, products) {
      return (
        entries.every(
          e =>
            e.paymentStatus == "PAID" ||
            e.paidExternally ||
            e.competition.paidExternally ||
            e.competition.isAdministrative ||
            e.paymentStatus == null
        ) &&
        products.every(
          p =>
            p.paymentStatus == "PAID" ||
            p.paymentStatus == null ||
            p.paidExternally
        )
      );
    },
    registrantComments(entries) {
      return entries.filter(o => o.registrantComment || o.noteToFeiOrganiser);
    },
    timestamps(reg) {
      const entries = reg.entries.map(entry => {
        const section =
          entry.sectionId &&
          this.getSectionFromMaster(entry.sectionId, "shortName")
            ? ` (${this.getSectionFromMaster(entry.sectionId, "shortName")})`
            : "";
        const direction = get(
          entry.competition,
          "competitionRule.icelandicDirectionRequired",
          null
        )
          ? " - " + entry.icelandicDirection
          : "";
        return {
          name: `${entry.competition.code}${section}${direction}`,
          paidAt: entry.paidAt,
          createdAt: entry.createdAt,
          updatedAt: entry.updatedAt,
          unregisteredAt: entry.unregisteredAt
        };
      });
      const eventProducts = reg.registrationEventProducts.map(regEP => ({
        name: regEP.eventProduct.name,
        paidAt: regEP.paidAt,
        createdAt: regEP.createdAt,
        updatedAt: regEP.updatedAt,
        unregisteredAt: regEP.cancelledAt
      }));

      return [...entries, ...eventProducts];
    },
    expand(id) {
      if (this.expanded.includes(id)) {
        this.expanded = this.expanded.filter(x => x != id);
      } else {
        this.expanded.push(id);
      }
    }
  },
  fragments: {
    registrations: registrationFragment
  }
};
</script>
